import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Check, ClipboardList, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';

import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';

const STATUS_META = {
  sent:    { label: 'Sent',    pill: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  skipped: { label: 'Skipped', pill: 'bg-amber-100  text-amber-700  border-amber-200' },
  failed:  { label: 'Failed',  pill: 'bg-rose-100   text-rose-700   border-rose-200' },
};

const DAYS_OPTIONS = [
  { value: 7,   label: 'Last 7 days' },
  { value: 30,  label: 'Last 30 days' },
  { value: 90,  label: 'Last 90 days' },
  { value: 180, label: 'Last 180 days' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd-MMM-yy'); } catch { return iso; }
};
const formatSentAt = (iso) => {
  if (!iso) return '—';
  try { return format(new Date(iso), 'dd-MMM HH:mm'); } catch { return iso; }
};

export default function ReviewLogPage() {
  const { token } = useAuth();
  const { getVenueNames } = useMasterData();

  const [days, setDays] = useState(30);
  const [statusFilter, setStatusFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const venueOptions = useMemo(() => getVenueNames(), [getVenueNames]);

  const fetchLog = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = { days, status: statusFilter };
      if (venueFilter && venueFilter !== 'all') params.venue = venueFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/whatsapp-analytics/review-log`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Review log fetch error', err);
      toast.error('Failed to load review log');
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter, venueFilter, search, token]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  const summary = data?.summary || { sent: 0, skipped: 0, failed: 0, total: 0 };
  const rows = data?.rows || [];

  const clearSearch = () => { setSearch(''); };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="review-log-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight flex items-center gap-2">
              <ClipboardList className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
              WhatsApp Review Log
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">
              Every post-game review message — sent, skipped (cooldown), or failed. One entry per session.
            </p>
          </div>
          <Button
            onClick={fetchLog}
            disabled={loading}
            variant="outline"
            data-testid="review-log-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-3 sm:p-4" data-testid="review-log-summary-total">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Total</p>
            <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{summary.total}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-emerald-200 bg-emerald-50/40" data-testid="review-log-summary-sent">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-700">Sent</p>
            <p className="text-xl sm:text-2xl font-bold text-emerald-700 mt-1">{summary.sent}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-amber-200 bg-amber-50/40" data-testid="review-log-summary-skipped">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-amber-700">Skipped</p>
            <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1">{summary.skipped}</p>
          </Card>
          <Card className="p-3 sm:p-4 border-rose-200 bg-rose-50/40" data-testid="review-log-summary-failed">
            <p className="text-[10px] sm:text-xs uppercase tracking-wider text-rose-700">Failed</p>
            <p className="text-xl sm:text-2xl font-bold text-rose-700 mt-1">{summary.failed}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4" data-testid="review-log-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Window</label>
              <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
                <SelectTrigger data-testid="review-log-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_OPTIONS.map((d) => (
                    <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="review-log-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Venue</label>
              <Select value={venueFilter} onValueChange={setVenueFilter}>
                <SelectTrigger data-testid="review-log-venue-select">
                  <SelectValue placeholder="All Venues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {venueOptions.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Search name / mobile</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. 99999 or Mr. Sharma"
                  className="pl-9 pr-9"
                  data-testid="review-log-search-input"
                />
                {search && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="review-log-clear-search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="overflow-visible">
          {loading ? (
            <div className="p-12 text-center text-slate-500">Loading review log…</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-slate-500" data-testid="review-log-empty">
              No review records match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="review-log-table">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Sent At</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Session Date</th>
                    <th className="px-3 py-3 text-left font-semibold">Player</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Mobile</th>
                    <th className="px-3 py-3 text-left font-semibold">Venue</th>
                    <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Time</th>
                    <th className="px-3 py-3 text-center font-semibold">Slots</th>
                    <th className="px-3 py-3 text-center font-semibold">Status</th>
                    <th className="px-3 py-3 text-left font-semibold">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.failed;
                    return (
                      <tr
                        key={r.booking_id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`review-log-row-${r.booking_id}`}
                      >
                        <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{formatSentAt(r.review_sent_at)}</td>
                        <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{formatDate(r.date)}</td>
                        <td className="px-3 py-2.5 text-slate-900 font-medium">
                          {r.player_name || '—'}
                          {r.game && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">{r.game}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 font-mono text-xs whitespace-nowrap">{r.player_mobile || '—'}</td>
                        <td className="px-3 py-2.5 text-slate-700">{r.venue}</td>
                        <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                          {r.start_time && r.end_time ? `${r.start_time} – ${r.end_time}` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-center text-slate-700">
                          {r.slot_count > 1 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium" title="Consolidated multi-slot session">
                              {r.slot_count}×
                            </span>
                          ) : (
                            <span className="text-slate-400">1</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.pill}`}>
                            {r.status === 'sent' && <Check className="w-3 h-3" />}
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs">
                          {r.reason ? <span data-testid={`review-log-reason-${r.booking_id}`}>{r.reason}</span> : <span className="text-slate-300">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!loading && data && data.total > rows.length && (
            <p className="px-3 py-2 text-xs text-slate-400 text-right">
              Showing {rows.length} of {data.total} — refine filters to narrow.
            </p>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
